import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import RouterService from '@ember/routing/router-service';
import { task, timeout } from 'ember-concurrency';
import HydraService from 'cio/services/hydra';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import UserModel from 'cio/models/user';
import EnvironmentModel from 'cio/models/environment';
import DS from 'ember-data';
import config from 'cio/config/environment';
import { announceRefreshWorkspaces } from '@customerio/hydra-nav-state';
import AccountService from 'cio/services/account';
import { ProductApp } from 'cio/utils/product-app-info';

interface HydraWorkspace {
  id: number;
  cio_initialized: boolean;
  cdp_initialized: boolean;
}
export default class SettingsWorkspacesNewController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service hydra!: HydraService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service account!: AccountService;

  declare model: {
    environment: EnvironmentModel;
    users: UserModel[];
  };

  get cannotCreateWorkspaces() {
    return !this.auth.hasPermission('account/workspaces/create');
  }

  @action
  cancel() {
    this.router.transitionTo('settings.workspaces');
  }

  // The SettingsEnvironmentForm component expects a
  // `hydraUsersPermissions` argument to be present for editing
  // workspaces while the `workspaces` endpoint doesn't return the
  // `user_ids` field. For new workspaces, we need to generate the
  // values manually.
  //
  // TODO: Remove once/if user_ids comes from the `workspaces`
  // endpoint, or other similar backend changes.
  get hydraUsersPermissions() {
    return {
      user_ids: this.model.users.map((user) => user.id),
    };
  }

  createWorkspaceViaHydra = task({ drop: true }, async () => {
    let { environment } = this.model;

    let response: { id: string | number };
    try {
      response = await this.api.post('workspaces', {
        pathScope: 'hydra_account',
        ignoreErrors: true,
        data: {
          name: environment.name,
          color: environment.hexCode,
          user_ids: environment
            .hasMany('users')
            .ids()
            .map((id) => parseInt(id)),
          users: (environment.users.content as UserModel[]).map((user) => {
            return {
              id: parseInt(user.id),
              role: user.role,
            };
          }),
          cio: {
            delivery_settings: environment.deliverySettings,
            delivery_email: environment.deliveryEmail,
            email_open_tracking: environment.emailOpenTracking,
            identifier_type: environment.identifierType,
            identifier_settings: environment.identifierSettings,
            identifiers: environment.identifiers,
          },
        },
      });
    } catch (e) {
      if (e.errors?.[0]?.status === '409') {
        // If the workspace already exists, stop polling and allow showing
        // validation errors
        e.errors.forEach((error: any) => {
          // Like the environment adapter, we check if the error message
          // matches "already exists"
          let isAlreadyExistsError = error.detail != null && /Workspace.*already exists/i.test(error.detail);

          if (isAlreadyExistsError) {
            this.model.environment.errors.add('name', 'A workspace with this name already exists');
          }
        });
      }
      throw e;
    }

    let workspaceCreated = false;

    // Wait for the Journeys workspace to be created
    while (!workspaceCreated) {
      await timeout(config.APP.workspacePollDelay);
      workspaceCreated = await this.isWorkspaceReady(response.id);
    }

    announceRefreshWorkspaces();
  });

  private async isWorkspaceReady(newWorkspaceId: number | string) {
    try {
      let hydraWorkspace = await this.api.request('workspaces', { pathScope: 'hydra_account' });
      for (let workspace of hydraWorkspace) {
        if (workspace.id === newWorkspaceId && this.areAllProductsReady(workspace)) {
          await this.store.findRecord('environment', newWorkspaceId);
          return true;
        }
      }
    } catch (e) {
      if (e.errors?.[0]?.status === '404') {
        // Do nothing, continue polling
      } else {
        // If there's an unexpected error, rethrow it
        throw e;
      }
    }
    return false;
  }

  private areAllProductsReady(workspace: HydraWorkspace) {
    if (this.isJourneysActive && !workspace.cio_initialized) {
      return false;
    }
    if (this.isPipelinesActive && !workspace.cdp_initialized) {
      return false;
    }
    return true;
  }

  get isJourneysActive() {
    return this.account.accessibleProducts.includes(ProductApp.JOURNEYS);
  }

  get isPipelinesActive() {
    return this.account.accessibleProducts.includes(ProductApp.DATA_PIPELINES);
  }

  submit = task({ drop: true }, async () => {
    await this.createWorkspaceViaHydra.perform();

    await this.auth.user?.reload();

    this.alerts.success('environment.new.success', `${this.model.environment.name} workspace created`);
    this.router.transitionTo('settings.workspaces');
  });
}
